// =============================================================================
// Auth slice
//
// ── TWO-IDENTITY MODEL (admin preview mode) ──────────────────────────────────
//
// When an admin activates "view as user" mode the Redux store holds TWO
// distinct identities simultaneously:
//
//   state.user        — the REAL signed-in admin.
//                       Cognito session and JWT always belong to this person.
//                       Used for: API authentication, anything that must
//                       execute with real privileges (uploads, mutations).
//
//   state.viewAsUser  — the PREVIEWED user's public profile snapshot.
//                       No JWT. Used only to simulate what that user sees.
//                       Used for: UI permission checks, display logic.
//
// ── RULE OF THUMB ─────────────────────────────────────────────────────────────
//
//   "Am I deciding what to SHOW (menus, buttons, access badges)?"
//     → use the EFFECTIVE identity:
//         const effectiveUser = viewAsUser ?? user
//         const effectiveRole = viewAsUser?.role ?? viewAsRole ?? user?.role
//
//   "Am I making an API call or attaching a JWT?"
//     → always use the REAL user (apiFetch handles this automatically).
//       For server-side visibility simulation pass ?previewAs=<userId> and
//       let the backend re-apply the previewed user's access rules.
//
// ── REFERENCE IMPLEMENTATION ──────────────────────────────────────────────────
//
//   NavBar.tsx is the canonical example:
//     const effectiveRole = viewAsUser?.role ?? viewAsRole ?? user?.role;
//     const isAdmin = effectiveRole === 'admin';
//
//   LibraryPage.tsx shows the ownership variant:
//     const effectiveUser = viewAsUser ?? user;
//     canModify = effectiveUser?.role === 'admin' || doc.uploadedBy === effectiveUser?.userId
//
// ── WHAT viewAsUser CONTAINS ──────────────────────────────────────────────────
//
//   { userId, username, displayName?, avatarUrl?, role }
//
//   It does NOT contain world memberships, campaign assignments, or any other
//   data that requires a DynamoDB lookup.  For server-side checks that need
//   those (e.g. library visibility), pass ?previewAs=<userId> so the backend
//   can query the real data under the admin's credentials.
//
// =============================================================================

import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import { signInWithRedirect, fetchAuthSession, signOut } from 'aws-amplify/auth';
import { apiFetch } from '@/lib/api';

export interface AuthUser {
  userId: string;
  /** Discord handle from DynamoDB — used as fallback when displayName is not set. */
  username: string;
  /** User-chosen display name. Falls back to username in the UI when absent. */
  displayName?: string;
  /** Current avatar URL — either the Discord CDN URL or a pre-signed S3 URL. */
  avatarUrl?: string;
  /** S3 object key of the custom uploaded avatar. Absent when using Discord avatar. */
  avatarKey?: string;
  role: 'user' | 'dm' | 'moderator' | 'admin';
  discordId?: string;
}

/**
 * Snapshot of a specific user's public profile used for admin "preview as user" mode.
 * When set, the UI renders as if the admin were logged in as this person.
 * Mutually exclusive with viewAsRole — setting one clears the other.
 *
 * ⚠️  This is NOT a JWT substitute.  apiFetch always uses the real admin's
 * Cognito token.  All UI permission checks that depend on identity (role gates,
 * ownership checks, "can I edit this?" logic) must use the effective identity:
 *
 *   const effectiveUser = viewAsUser ?? user;
 *
 * See the module-level comment for the full two-identity model.
 */
export interface ViewAsUser {
  userId: string;
  username: string;
  displayName?: string;
  avatarUrl?: string;
  role: AuthUser['role'];
}

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  isLoading: boolean;
  error: string | null;
  /** Admin-only: temporarily simulate viewing the site as a different role (no specific user). */
  viewAsRole: AuthUser['role'] | null;
  /** Admin-only: simulate viewing the site as a specific user (inherits their role + data). */
  viewAsUser: ViewAsUser | null;
  /**
   * Set of userId strings for users who have been granted the Supporter badge.
   * Loaded at app startup from GET /users/supporters (public, no auth required).
   * Used throughout the UI to display the Gilded badge on those users' identities.
   */
  supporterUserIds: string[];
}

const initialState: AuthState = {
  user: null,
  accessToken: null,
  isLoading: true,
  error: null,
  viewAsRole: null,
  viewAsUser: null,
  supporterUserIds: [],
};

interface UserProfile {
  userId: string;
  username: string;
  displayName?: string;
  avatarUrl?: string;
  avatarKey?: string;
}

export const initializeAuth = createAsyncThunk('auth/initialize', async () => {
  // ── Session check first ──────────────────────────────────────────────────
  // We must await fetchAuthSession() before making any apiFetch calls.
  // apiFetch calls getToken() → fetchAuthSession() internally; if we fire it
  // before Amplify has hydrated its token cache from localStorage, getToken()
  // returns undefined, the API Gateway JWT authorizer rejects the request with
  // 401, and the .catch() silently returns [].  Awaiting the session here
  // ensures the token is in Amplify's cache for all subsequent apiFetch calls.
  const session = await fetchAuthSession();

  if (!session.tokens?.accessToken) {
    // Not logged in — /users/supporters is public (NONE auth) so fetch it
    // anyway so anonymous visitors still see supporter badges on boards/library.
    const supporterUserIds = await apiFetch<{ supporters: string[] }>('/users/supporters')
      .then((r) => r.supporters)
      .catch(() => [] as string[]);
    return { user: null, accessToken: null, supporterUserIds };
  }

  const claims     = session.tokens.accessToken.payload;
  const accessToken = session.tokens.accessToken.toString();
  // custom:user_id is injected by preTokenGeneration as event.userName
  // ('discord_<id>'), matching the DynamoDB PK written by postConfirmation.
  const userId    = (claims['custom:user_id'] as string) ?? (claims.sub as string);
  const role      = ((claims['custom:role'] as string) ?? 'user') as AuthUser['role'];
  const discordId = (claims['custom:discord_id'] as string) ?? undefined;

  // ── Parallel data fetches ────────────────────────────────────────────────
  // Token is now in Amplify's cache, so both calls will include the JWT.
  // Running them in parallel shaves ~200–400 ms off cold startup.
  const [profile, supporterUserIds] = await Promise.all([
    apiFetch<UserProfile>('/users/me').catch(() => null as UserProfile | null),
    apiFetch<{ supporters: string[] }>('/users/supporters')
      .then((r) => r.supporters)
      .catch(() => [] as string[]),
  ]);

  return {
    user: {
      userId,
      username:        profile?.username    ?? (claims['cognito:username'] as string) ?? '',
      displayName:     profile?.displayName,
      avatarUrl:       profile?.avatarUrl,
      avatarKey:       profile?.avatarKey,
      role,
      discordId,
    },
    accessToken,
    supporterUserIds,
  };
});

export const loginWithDiscord = createAsyncThunk('auth/loginWithDiscord', async () => {
  await signInWithRedirect({ provider: { custom: 'Discord' } });
});

export const logout = createAsyncThunk('auth/logout', async () => {
  await signOut();
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser(state, action: PayloadAction<AuthUser>) {
      state.user = action.payload;
    },
    setDisplayName(state, action: PayloadAction<string>) {
      if (state.user) state.user.displayName = action.payload;
    },
    setAvatar(state, action: PayloadAction<{ avatarUrl: string; avatarKey: string }>) {
      if (state.user) {
        state.user.avatarUrl = action.payload.avatarUrl;
        state.user.avatarKey = action.payload.avatarKey;
      }
    },
    clearAvatar(state, action: PayloadAction<{ avatarUrl?: string }>) {
      if (state.user) {
        state.user.avatarUrl = action.payload.avatarUrl;
        state.user.avatarKey = undefined;
      }
    },
    setViewAsRole(state, action: PayloadAction<AuthUser['role'] | null>) {
      // Only admins may enter "view as" mode; mutually exclusive with viewAsUser.
      state.viewAsRole = action.payload;
      state.viewAsUser = null;
    },
    setViewAsUser(state, action: PayloadAction<ViewAsUser | null>) {
      // Preview as a specific user — inherits their role, data, and content ownership.
      // Mutually exclusive with viewAsRole.
      state.viewAsUser = action.payload;
      state.viewAsRole = null;
    },
    /** Replace the full supporter list (e.g. after a hard refresh). */
    setSupporterUserIds(state, action: PayloadAction<string[]>) {
      state.supporterUserIds = action.payload;
    },
    /**
     * Toggle a single user's supporter status in the local store immediately,
     * so the Admin Panel reflects the change without waiting for a re-fetch.
     */
    updateSupporterStatus(state, action: PayloadAction<{ userId: string; isSupporter: boolean }>) {
      const { userId, isSupporter } = action.payload;
      if (isSupporter) {
        if (!state.supporterUserIds.includes(userId)) {
          state.supporterUserIds.push(userId);
        }
      } else {
        state.supporterUserIds = state.supporterUserIds.filter((id) => id !== userId);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializeAuth.pending, (state) => { state.isLoading = true; })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.isLoading = false;
        if (!action.payload) return;
        state.supporterUserIds = action.payload.supporterUserIds;
        if (action.payload.user) {
          state.accessToken = action.payload.accessToken;
          state.user = action.payload.user;
        }
      })
      .addCase(initializeAuth.rejected, (state) => { state.isLoading = false; })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.accessToken = null;
        state.viewAsRole = null;
        state.viewAsUser = null;
        // Supporter list is public — keep it across logouts so pages still render correctly.
      });
  },
});

export const {
  setUser, setDisplayName, setAvatar, clearAvatar,
  setViewAsRole, setViewAsUser,
  setSupporterUserIds, updateSupporterStatus,
} = authSlice.actions;
export default authSlice.reducer;
